import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, ChevronUp, GripVertical, Trash2 } from 'lucide-react'

import React from 'react'

import { FieldSchema } from '@/lib/builder/schemas'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'

interface SortableItemProps {
  idx: number
  item: any
  field: FieldSchema
  value: any[]
  expandedIndex: number | null
  handleToggle: (idx: number) => void
  handleRemove: (e: React.MouseEvent, idx: number) => void
  onChange: (value: any[]) => void
  onImageRequest: (path: string) => void
  path: string
  renderField: (
    field: FieldSchema,
    value: any,
    onChange: (val: any) => void,
    path: string
  ) => React.ReactNode
}

export const SortableItem = ({
  idx,
  item,
  field,
  value,
  expandedIndex,
  handleToggle,
  handleRemove,
  onChange,
  onImageRequest,
  path,
  renderField,
}: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: idx.toString(),
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 0,
    position: 'relative' as any,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="overflow-hidden rounded-lg border border-border bg-card transition-shadow"
    >
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30"
        onClick={() => handleToggle(idx)}
      >
        <div className="flex items-center gap-3">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1">
            <GripVertical className="h-4 w-4 text-muted-foreground/30" />
          </div>
          <span className="text-overline uppercase text-muted-foreground">
            Item #{idx + 1}
          </span>
          {(item.label || item.title || item.name) && (
            <span className="text-xs font-bold truncate max-w-[200px] border-l pl-3 ml-3">
              {item.label || item.title || item.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => handleRemove(e, idx)}
            className="h-8 w-8 text-destructive hover:bg-destructive/10 rounded-full"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {expandedIndex === idx ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>
      {expandedIndex === idx && field.itemSchema && (
        <div className="p-6 border-t bg-muted/[0.03] space-y-6 animate-in slide-in-from-top-2 duration-300">
          {field.itemSchema.map((subField: any) => {
            if (subField.dependency) {
              const dependencyValue = item[subField.dependency.field]
              if (dependencyValue !== subField.dependency.value) {
                return null
              }
            }
            return (
              <div key={subField.key} className="space-y-2">
                <Label className="text-subtitle2 text-ink">
                  {subField.label}
                </Label>
                {renderField(
                  subField,
                  item[subField.key],
                  (v) => {
                    const newList = [...value]
                    newList[idx] = { ...newList[idx], [subField.key]: v }
                    onChange(newList)
                  },
                  `${path ? path : field.key}.${idx}.${subField.key}`
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
